/**
 * useRoleCapabilities — GAP-P1-ROLECAP
 * =====================================
 *
 * Maps the current workspace role to engine-review capabilities so review
 * surfaces (CalculationReviewPanel + EngineDetailViews) can render for
 * every role instead of requiring the external-accountant JWT context.
 *
 * Role source: `useWorkspaceRole` — the EXISTING tri-backend role
 * normalizer (Clerk metadata/Organizations → AttorneyAuthContext →
 * AccountantAuthContext). No new auth path is introduced here; this hook
 * only maps that role onto a capability set.
 *
 * Capability matrix (spec: docs/plans/2026-07-24-phase1-orchestrator-gaps.md
 * §GAP-P1-ROLECAP):
 *
 *   | role                        | engineReview   | canAttest | canOverride |
 *   |-----------------------------|----------------|-----------|-------------|
 *   | accountant (int/ext)        | 'full'         | true      | true        |
 *   | attorney / adjuster         | 'read+dispute' | false     | false       |
 *   | data-specialist             | 'read'         | false     | false       |
 *   | unknown / anything else     | 'read'         | false     | false       |
 *
 * Unknown roles FAIL CLOSED to read-only. Note the distinction consumers
 * must honor: `role === null` from the hook means NO auth backend could be
 * detected at all (e.g. legacy surfaces / tests rendered without any
 * provider) — consumers that predate this hook treat null as "preserve
 * legacy mode-driven behavior" so existing accountant surfaces stay
 * byte-identical, while any DETECTED role applies the matrix above.
 */

import { useWorkspaceRole } from './useWorkspaceRole';
import type { WorkspaceRole } from '../config/toolsManifest';
import type { Stage } from '../lib/casePath';

export type EngineReviewCapability = 'full' | 'read+dispute' | 'read';

export interface RoleCapabilities {
  /** What the role may do on engine-review surfaces. */
  engineReview: EngineReviewCapability;
  /** May sign attestations (approve / finalize flows). */
  canAttest: boolean;
  /** May add / inactivate engine overrides. */
  canOverride: boolean;
}

const FULL: RoleCapabilities = Object.freeze({
  engineReview: 'full' as const,
  canAttest: true,
  canOverride: true,
});

const READ_DISPUTE: RoleCapabilities = Object.freeze({
  engineReview: 'read+dispute' as const,
  canAttest: false,
  canOverride: false,
});

const READ_ONLY: RoleCapabilities = Object.freeze({
  engineReview: 'read' as const,
  canAttest: false,
  canOverride: false,
});

/**
 * Pure role → capabilities mapping (unit-testable without React).
 *
 * Accepts any string so future roles (e.g. 'adjuster', which is not yet a
 * WorkspaceRole literal) resolve without a type change. Hyphen/underscore
 * and case variants are normalized ('data-specialist' === 'data_specialist').
 */
export function capabilitiesForRole(
  role: string | null | undefined,
): RoleCapabilities {
  if (!role) return READ_ONLY;

  const normalized = role.toLowerCase().trim().replace(/-/g, '_');

  switch (normalized) {
    // Accountant family — the attesting reviewers.
    case 'accountant':
    case 'accountant_internal':
    case 'accountant_external':
      return FULL;

    // Internal platform-staff family — owner/admin/staff, plus 'analyst' (the
    // useWorkspaceRole DEFAULT for Clerk users with no explicit role metadata).
    // Fail-SAFE to full here: mapping these to read-only would strip the action
    // bar from internal reviewers (including the owner account) the moment a
    // role is detected — the same truthy-sentinel/staff class of bug that bit
    // the WorkDesk twice. External boundaries stay enforced by the backend.
    case 'owner':
    case 'admin':
    case 'staff':
    case 'analyst':
      return FULL;

    // Counsel + carrier-side reviewers: may read and raise disputes, but
    // never approve/override/waive — role boundaries per CLAUDE.md
    // ("forensic agents calculate, attorney agents interpret, adjuster
    // agents negotiate") apply to humans on this surface too.
    case 'attorney':
    case 'adjuster':
      return READ_DISPUTE;

    case 'data_specialist':
      return READ_ONLY;

    // Unknown roles fail closed.
    default:
      return READ_ONLY;
  }
}

/**
 * stageLensFor — GAP-P2-ROLE-LENSES
 * ==================================
 *
 * Maps a workspace role onto the case-path stages it OPERATES (does the work
 * for) vs OBSERVES (should see, muted, without being the acting party). Pure
 * and unit-testable, same shape as `capabilitiesForRole`.
 *
 * Matrix:
 *   | role                    | operates                          | observes                          |
 *   |--------------------------|-----------------------------------|-----------------------------------|
 *   | data_specialist          | setup, foundation                 | method, calculate, attest, deliver|
 *   | accountant (int/ext)     | foundation, method, calculate     | setup, attest, deliver            |
 *   | attorney                 | calculate, attest, deliver        | setup, foundation, method         |
 *   | adjuster                 | (none)                            | calculate, attest, deliver        |
 *   | owner/admin/staff/analyst| ALL SIX (fail-safe, mirrors the   | (none)                            |
 *   |                          | capabilitiesForRole FULL switch)  |                                    |
 *   | unknown/null/undefined   | (none)                            | (none) — the NEUTRAL lens         |
 *
 * IMPORTANT: unknown/undefined roles resolve to the NEUTRAL lens (empty
 * operates AND empty observes) — NOT all-operates and NOT all-observes. This
 * is what lets CasePathShell render byte-identical to its pre-lens behavior
 * when no `role` prop is supplied: an absent role must never light up (or
 * mute) every stage.
 *
 * adjuster is intentionally asymmetric: it observes only the negotiation-
 * relevant tail (calculate/attest/deliver) — setup/foundation/method are
 * neither operated nor observed, i.e. simply not adjuster's concern.
 */

export interface StageLens {
  /** Stages this role is the acting party for — normal (non-muted) styling. */
  operates: Stage[];
  /** Stages this role should see but does not act on — muted styling. */
  observes: Stage[];
}

const ALL_STAGES: Stage[] = ['setup', 'foundation', 'method', 'calculate', 'attest', 'deliver'];

const NEUTRAL_LENS: StageLens = { operates: [], observes: [] };

export function stageLensFor(role: string | null | undefined): StageLens {
  if (!role) return { ...NEUTRAL_LENS };

  const normalized = role.toLowerCase().trim().replace(/-/g, '_');

  switch (normalized) {
    case 'data_specialist':
      return { operates: ['setup', 'foundation'], observes: ['method', 'calculate', 'attest', 'deliver'] };

    // Accountant family — mirrors capabilitiesForRole's FULL grouping.
    case 'accountant':
    case 'accountant_internal':
    case 'accountant_external':
      return { operates: ['foundation', 'method', 'calculate'], observes: ['setup', 'attest', 'deliver'] };

    case 'attorney':
      return { operates: ['calculate', 'attest', 'deliver'], observes: ['setup', 'foundation', 'method'] };

    // Adjuster never operates a case-path stage (negotiates off the output);
    // it observes only the tail it cares about — see note above.
    case 'adjuster':
      return { operates: [], observes: ['calculate', 'attest', 'deliver'] };

    // Internal platform-staff family — mirrors the existing fail-safe FULL
    // switch in capabilitiesForRole (owner/admin/staff + 'analyst', the
    // useWorkspaceRole default for Clerk users with no explicit role
    // metadata). Full visibility, nothing muted.
    case 'owner':
    case 'admin':
    case 'staff':
    case 'analyst':
      return { operates: [...ALL_STAGES], observes: [] };

    // Unknown roles resolve to the neutral lens (fail closed to "no lens
    // styling applied", not "everything muted" or "everything emphasized").
    default:
      return { ...NEUTRAL_LENS };
  }
}

export interface RoleCapabilitiesResult extends RoleCapabilities {
  /**
   * The detected workspace role, or null when no auth backend is active
   * (no Clerk mount, no attorney/accountant JWT context). Consumers use
   * null to distinguish "indeterminate" from "known-but-restricted".
   */
  role: WorkspaceRole | null;
}

/**
 * React hook: current role → capabilities.
 *
 * Never throws — `useWorkspaceRole` is read inside a try/catch (same
 * guarded-hook pattern useWorkspaceRole itself uses for its optional
 * contexts) so surfaces render even when no auth provider is mounted.
 */
export function useRoleCapabilities(): RoleCapabilitiesResult {
  let role: WorkspaceRole | null = null;
  try {
    role = useWorkspaceRole();
  } catch {
    // No auth backend mounted (e.g. Clerk hooks outside ClerkProvider in
    // an isolated render). Treat as indeterminate.
    role = null;
  }
  return { role, ...capabilitiesForRole(role) };
}

export default useRoleCapabilities;
